import React, { useState, useEffect } from 'react';
import { Link, To } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    maxVisiblePages?: number;
    showPageInput?: boolean;
    // When set, pages render as real links (crawlable, open in a new tab) and
    // navigation is left to the router; onPageChange then serves only the
    // jump-to-page input.
    getPageLink?: (page: number) => To;
}

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    maxVisiblePages = 5,
    showPageInput = true,
    getPageLink,
}) => {
    const [inputValue, setInputValue] = useState('');
    const [showInput, setShowInput] = useState(false);

    useEffect(() => {
        setInputValue(currentPage.toString());
    }, [currentPage]);

    if (totalPages <= 1) return null;

    const generatePageNumbers = (): (number | string)[] => {
        const pages: (number | string)[] = [];

        if (totalPages <= maxVisiblePages + 2) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }

        const sidePages = Math.floor((maxVisiblePages - 3) / 2);
        const startPage = Math.max(2, currentPage - sidePages);
        const endPage = Math.min(totalPages - 1, currentPage + sidePages);

        pages.push(1);

        if (startPage > 2) {
            pages.push('...');
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        if (endPage < totalPages - 1) {
            pages.push('...');
        }

        pages.push(totalPages);

        return pages;
    };

    const pageNumbers = generatePageNumbers();

    const renderPageControl = (
        page: number,
        className: string,
        ariaLabel: string,
        content: React.ReactNode,
        isCurrent = false,
    ) => {
        const shared = {
            className,
            'aria-label': ariaLabel,
            'aria-current': isCurrent ? ('page' as const) : undefined,
        };
        return getPageLink
            ? <Link key={ariaLabel} to={getPageLink(page)} {...shared}>{content}</Link>
            : <button key={ariaLabel} onClick={() => onPageChange(page)} {...shared}>{content}</button>;
    };

    const disabledArrow = (ariaLabel: string, content: React.ReactNode) => (
        <button disabled className="p-2 rounded-md transition-colors text-gray-400 cursor-not-allowed" aria-label={ariaLabel}>
            {content}
        </button>
    );

    const handleInputSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const pageNum = parseInt(inputValue);
        if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
            onPageChange(pageNum);
            setShowInput(false);
        } else {
            // Reset to current page if invalid
            setInputValue(currentPage.toString());
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Only allow numbers
        if (value === '' || /^\d+$/.test(value)) {
            setInputValue(value);
        }
    };

    const handleInputBlur = () => {
        // Reset to current page if user clicks away without submitting
        setInputValue(currentPage.toString());
        setShowInput(false);
    };

    return (
        <div className="flex flex-col items-center mt-8 space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4">
            <nav className="flex items-center space-x-1">
                {currentPage === 1
                    ? disabledArrow('Previous page', <ChevronLeft className="h-5 w-5" />)
                    : renderPageControl(
                        currentPage - 1,
                        'p-2 rounded-md transition-colors text-gray-600 hover:bg-gray-100',
                        'Previous page',
                        <ChevronLeft className="h-5 w-5" />,
                    )}

                <div className="flex items-center space-x-1">
                    {pageNumbers.map((pageNumber, index) => {
                        if (pageNumber === '...') {
                            return (
                                <span
                                    key={`ellipsis-${index}`}
                                    className="px-3 py-1 text-gray-500"
                                >
                                    ...
                                </span>
                            );
                        }

                        const page = pageNumber as number;
                        return renderPageControl(
                            page,
                            `inline-block text-center px-3 py-1 rounded-md transition-colors min-w-[2.5rem] ${
                                currentPage === page
                                    ? 'bg-primary-600 text-white font-medium'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`,
                            `Go to page ${page}`,
                            page,
                            currentPage === page,
                        );
                    })}
                </div>

                {currentPage === totalPages
                    ? disabledArrow('Next page', <ChevronRight className="h-5 w-5" />)
                    : renderPageControl(
                        currentPage + 1,
                        'p-2 rounded-md transition-colors text-gray-600 hover:bg-gray-100',
                        'Next page',
                        <ChevronRight className="h-5 w-5" />,
                    )}
            </nav>

            <div className="flex items-center space-x-3 text-sm text-gray-600">
                {showPageInput && totalPages > 10 && (
                    <div className="flex items-center space-x-2">
                        {showInput ? (
                            <form onSubmit={handleInputSubmit} className="flex items-center space-x-1">
                                <span className="hidden sm:inline">Към страница</span>
                                <span className="sm:hidden">Стр.</span>
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    onBlur={handleInputBlur}
                                    className="w-12 px-2 py-1 text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    autoFocus
                                    maxLength={String(totalPages).length}
                                    placeholder={currentPage.toString()}
                                />
                                <span>от {totalPages}</span>
                            </form>
                        ) : (
                            <button
                                onClick={() => setShowInput(true)}
                                className="text-primary-600 hover:text-primary-700 hover:underline text-xs sm:text-sm"
                                title="Отиди на страница"
                            >
                                <span className="hidden sm:inline">Отиди на страница...</span>
                                <span className="sm:hidden">Стр...</span>
                            </button>
                        )}
                    </div>
                )}

                <span className="hidden sm:inline text-xs sm:text-sm">
                    Страница {currentPage} от {totalPages}
                </span>
                <span className="sm:hidden text-xs">
                    {currentPage}/{totalPages}
                </span>
            </div>
        </div>
    );
};

export default Pagination;